import React, { useMemo, useState } from "react"

import {
    faChevronRight,
    faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { PayerStatus } from "@common-shared/config/oneVerify/oneVerifyPayerStatus.config"
import type {
    IOneVerifyPayerStatusIncident,
    IOneVerifyPayerStatusPayer,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ButtonElement } from "@front-shared/elements/button.element"
import { InputElement } from "@front-shared/elements/input.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SelectElement } from "@front-shared/elements/select.element"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types"

import {
    PAYER_STATUS_CONFIG,
    PayerStatusRange,
} from "../../config/payerStatus.config"
import { ButtonDropdownElement } from "../../elements/buttonDropdown.element"
import { PayerStatusHelper } from "../../helpers/payerStatus.helper"
import { PayerStatusBadgeComponent } from "./payerStatusBadge.component"
import { PayerStatusMessageComponent } from "./payerStatusMessage.component"

import calendarIcon from "@front-shared/assets/images/icons/calendar-dark.svg"
import searchIcon from "@front-shared/assets/images/icons/search-dark.svg"

export const PayerStatusListComponent: React.FunctionComponent<
    IPayerStatusListComponentProps
> = props => {
    const [search, setSearch] = useState<string>("")

    // Search input isn't controlled, new key clears it
    const [searchInputKey, setSearchInputKey] = useState<number>(0)

    // Range issues are counted over, separate from timeline range
    const [issuesRange, setIssuesRange] = useState<PayerStatusRange>(
        PAYER_STATUS_CONFIG.defaultRange,
    )

    const statusOptions = useMemo<ISelectRenderedOption[]>(() => {
        return [
            { displayValue: "All statuses", value: null },

            ...[PayerStatus.ISSUE_DETECTED, PayerStatus.OPERATIONAL].map(
                status => ({
                    displayValue: PAYER_STATUS_CONFIG.statuses[status].label,
                    value: status,
                }),
            ),
        ]
    }, [])

    const issuesCountByPayerId = useMemo<Record<string, number>>(() => {
        const result: Record<string, number> = {}

        for (const incident of PayerStatusHelper.getIncidentsInRange(
            props.incidents,
            PayerStatusHelper.getRangeStart(issuesRange, props.fetchedAt),
        )) {
            result[incident.payerId] = (result[incident.payerId] || 0) + 1
        }

        return result
    }, [props.incidents, props.fetchedAt, issuesRange])

    // Payers with issue first, then most issues, then by name
    const rows = useMemo<IOneVerifyPayerStatusPayer[]>(() => {
        const query = search.trim().toLowerCase()

        return props.payers
            .filter(
                row =>
                    (!props.statusFilter ||
                        row.status === props.statusFilter) &&
                    (!query ||
                        PayerStatusHelper.getPayerName(row)
                            .toLowerCase()
                            .includes(query) ||
                        row.payerId.toLowerCase().includes(query)),
            )
            .sort(
                (a, b) =>
                    Number(b.status === PayerStatus.ISSUE_DETECTED) -
                        Number(a.status === PayerStatus.ISSUE_DETECTED) ||
                    (issuesCountByPayerId[b.payerId] || 0) -
                        (issuesCountByPayerId[a.payerId] || 0) ||
                    PayerStatusHelper.comparePayersByName(a, b),
            )
    }, [props.payers, props.statusFilter, search, issuesCountByPayerId])

    const isFiltered = !!search.trim() || !!props.statusFilter

    const issuesRangeLabel = PAYER_STATUS_CONFIG.rangeLabels[issuesRange]

    const issuesRangeShortLabel =
        PAYER_STATUS_CONFIG.rangeShortLabels[issuesRange]

    const onClearFilters = () => {
        setSearch("")
        setSearchInputKey(current => current + 1)

        props.onStatusFilterChange(null)
    }

    // Shown in table header, which is hidden on mobile, so on mobile it's next to payers count
    const renderIssuesRangeDropdown = (isHeader: boolean): React.ReactNode => {
        return (
            <ButtonDropdownElement
                className={
                    isHeader
                        ? "-mr-[10px] flex w-[150px] justify-end"
                        : "hidden sm:block"
                }
                buttonLabel={
                    isHeader
                        ? `Issues · last ${issuesRangeShortLabel}`
                        : `Last ${issuesRangeShortLabel}`
                }
                buttonType="default"
                // Icon is decorative, label would be read twice with its alt
                buttonIcon={
                    <img
                        src={calendarIcon}
                        alt=""
                        className={isHeader ? "ml-[8px]" : "mr-[8px]"}
                    />
                }
                buttonIsRightIcon={isHeader}
                buttonClassName={`!flex !h-auto !items-center !rounded-full !px-[10px] !py-[5px] !shadow-none ${
                    isHeader
                        ? "!border-0 !bg-transparent !font-mori-semibold !text-[12px] hover:!bg-brand-warmLight2"
                        : "!border !border-solid !border-brand-warmLight2 !bg-brand-white !text-[13px]"
                }`}
                dropdownClassName="!top-[calc(100%+6px)] w-[200px]"
                dropdownTitle="Count issues in the last"
                options={Object.values(PayerStatusRange).map(value => ({
                    label: PAYER_STATUS_CONFIG.rangeLabels[value],
                    isSelected: value === issuesRange,
                    onClick: () => {
                        setIssuesRange(value)
                    },
                }))}
            />
        )
    }

    const renderEmpty = (): React.ReactNode => {
        const [title, text, actionLabel] = search.trim()
            ? [
                  `No payers match “${search.trim()}”`,
                  "Check the spelling, or try the payer ID from the patient’s insurance card.",
                  "Clear search",
              ]
            : props.statusFilter === PayerStatus.ISSUE_DETECTED
              ? [
                    "No payers have an issue right now",
                    "Every payer is operating normally.",
                    "Show all payers",
                ]
              : [
                    "No payers to show",
                    "Try a different filter.",
                    "Show all payers",
                ]

        return (
            <PayerStatusMessageComponent
                icon={faMagnifyingGlass}
                title={title}
                text={text}
            >
                <ButtonElement
                    htmlType="button"
                    type="default"
                    size="middle"
                    label={actionLabel}
                    onClick={onClearFilters}
                />
            </PayerStatusMessageComponent>
        )
    }

    return (
        <section
            aria-label="Payer list"
            className={`flex min-w-0 flex-col gap-[14px] ${props.className || ""}`}
        >
            <div className="flex flex-wrap items-center justify-between gap-[12px]">
                <PrimaryText typography="h5">Payer list</PrimaryText>

                <div className="flex items-center gap-[12px]">
                    <div aria-live="polite">
                        <PrimaryText
                            typography="textSmall"
                            className="opacity-60"
                        >
                            {isFiltered
                                ? `${rows.length} of ${props.payers.length} payers`
                                : `${props.payers.length} payers`}
                        </PrimaryText>
                    </div>

                    {renderIssuesRangeDropdown(false)}
                </div>
            </div>

            <div className="flex flex-wrap items-end gap-[12px]">
                <InputElement
                    key={searchInputKey}
                    name="payerStatusSearch"
                    className="min-w-0 flex-[1_1_240px]"
                    placeholder="Search by payer name or payer ID"
                    onChange={event => {
                        setSearch(event.target.value)
                    }}
                    allowClear={true}
                    inputSuffix={
                        <img
                            src={searchIcon}
                            alt="Search"
                            title="Search"
                            className="ml-[5px]"
                        />
                    }
                />

                <SelectElement
                    name="payerStatusFilter"
                    className="min-w-[170px] flex-[0_1_200px]"
                    renderedOptions={statusOptions}
                    externalValue={
                        props.statusFilter
                            ? PAYER_STATUS_CONFIG.statuses[props.statusFilter]
                                  .label
                            : statusOptions[0].displayValue
                    }
                    onSelected={props.onStatusFilterChange}
                />
            </div>

            {rows.length ? (
                // Not clipped, so header dropdown can go over rows, header and rows round their own corners
                <div className="rounded-[16px] bg-brand-white shadow-sm sm:rounded-none sm:bg-transparent sm:shadow-none">
                    <div className="flex min-h-[44px] items-center gap-[16px] rounded-t-[16px] border-0 border-b border-solid border-brand-warmLight2 bg-brand-offWhite px-[20px] py-[8px] sm:hidden">
                        {/*Rows have their own labels*/}
                        <div
                            aria-hidden="true"
                            className="flex flex-1 items-center gap-[16px]"
                        >
                            <PrimaryText
                                typography="captionSemibold"
                                className="flex-1 opacity-60"
                            >
                                Payer name and ID
                            </PrimaryText>

                            <PrimaryText
                                typography="captionSemibold"
                                className="w-[200px] opacity-60"
                            >
                                Current status
                            </PrimaryText>
                        </div>

                        {renderIssuesRangeDropdown(true)}

                        <span className="w-[18px]" />
                    </div>

                    {/*AntD list margin isn't in a CSS layer, so it beats utility classes without !*/}
                    <ul className="!m-0 flex list-none flex-col overflow-hidden rounded-b-[16px] p-0 sm:gap-[10px] sm:overflow-visible sm:rounded-none">
                        {rows.map(row => {
                            const issuesCount =
                                issuesCountByPayerId[row.payerId] || 0

                            const issuesLabel = `${PayerStatusHelper.getIssuesCountLabel(issuesCount)} in the last ${issuesRangeLabel}`

                            return (
                                <li
                                    key={row.payerId}
                                    className="border-0 border-b border-solid border-brand-warmLight2 last:border-b-0 sm:border-b-0"
                                >
                                    <button
                                        type="button"
                                        aria-haspopup="dialog"
                                        aria-label={`${PayerStatusHelper.getPayerName(row)}, payer ID ${row.payerId}, ${
                                            PAYER_STATUS_CONFIG.statuses[
                                                row.status
                                            ].badgeLabel
                                        }, ${issuesLabel}. Open details`}
                                        className="flex min-h-[56px] w-full items-center gap-[16px] border-0 bg-brand-white px-[20px] py-[14px] text-left hover:bg-brand-warmLight025 focus-visible:outline-offset-[-2px] sm:rounded-[14px] sm:border sm:border-solid sm:border-brand-warmLight2 sm:p-[16px]"
                                        onClick={() => {
                                            props.onPayerClick(row.payerId)
                                        }}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <PrimaryText typography="textSemibold">
                                                {PayerStatusHelper.getPayerName(
                                                    row,
                                                )}
                                            </PrimaryText>

                                            <PrimaryText
                                                typography="textSmall"
                                                className="opacity-60"
                                            >
                                                {row.payerId}

                                                {issuesCount ? (
                                                    <span className="hidden sm:inline">
                                                        {` · ${issuesLabel}`}
                                                    </span>
                                                ) : null}
                                            </PrimaryText>

                                            {/*Badge is too wide to fit next to payer name on mobile*/}
                                            <div className="mt-[8px] hidden sm:block">
                                                <PayerStatusBadgeComponent
                                                    status={row.status}
                                                />
                                            </div>
                                        </div>

                                        <div className="w-[200px] sm:hidden">
                                            <PayerStatusBadgeComponent
                                                status={row.status}
                                            />
                                        </div>

                                        <PrimaryText
                                            className={`w-[140px] text-right sm:hidden ${issuesCount ? "" : "opacity-60"}`}
                                        >
                                            {issuesCount || "None"}
                                        </PrimaryText>

                                        <FontAwesomeIcon
                                            icon={faChevronRight}
                                            className="w-[18px] opacity-60"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            ) : (
                renderEmpty()
            )}
        </section>
    )
}

interface IPayerStatusListComponentProps {
    className?: string
    payers: IOneVerifyPayerStatusPayer[]
    incidents: IOneVerifyPayerStatusIncident[] // all of history
    fetchedAt: number
    statusFilter: PayerStatus | null
    onStatusFilterChange: (status: PayerStatus | null) => void
    onPayerClick: (payerId: string) => void
}
